import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

// Lesson: paid, limited spots. Players claim a spot through the event's share link,
// which runs the same register + Stripe flow as tryouts.
export const LessonSignupPanel = ({ event, onClaimSpot, onWalkUp }) => {
  const isFull = event.maxSpots && event.spotsRemaining === 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-2 text-sm text-gray-600">
        <span>
          <span className="font-medium text-gray-900">{event.spotsClaimed || 0}</span>
          {event.maxSpots ? ` of ${event.maxSpots} spots claimed` : ' spots claimed'}
        </span>
        {event.registrationFee > 0 && (
          <span>Fee: {formatMoney(event.registrationFee)}</span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={onClaimSpot}
          disabled={isFull}
          className="px-4 py-2 text-sm font-medium rounded-md bg-teal-600 text-white hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isFull ? 'Lesson Full' : 'Claim a Spot'}
        </button>
        {onWalkUp && !isFull && (
          <button
            onClick={onWalkUp}
            className="px-4 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors"
          >
            Walk-Up Registration
          </button>
        )}
      </div>
    </div>
  );
};

// Fundraiser: no registration. Anyone can donate through the share link; staff can
// see who gave.
export const FundraiserPanel = ({ event, onDonate, canViewDonors, authToken }) => {
  const [donations, setDonations] = useState(null);
  const [loadingDonations, setLoadingDonations] = useState(false);

  const raised = Number(event.amountRaised || 0);
  const goal = Number(event.fundraisingGoal || 0);
  const percent = goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : null;

  const toggleDonors = async () => {
    if (donations) {
      setDonations(null);
      return;
    }
    setLoadingDonations(true);
    try {
      const res = await axios.get(`/api/events/${event.id}/donations`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
      });
      setDonations(res.data.donations || []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load donations');
    } finally {
      setLoadingDonations(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1 text-sm">
        <span className="text-gray-900 font-medium">
          {formatMoney(raised)} raised
          {goal > 0 && <span className="text-gray-500 font-normal"> of {formatMoney(goal)} goal</span>}
        </span>
        <span className="text-gray-500">
          {event.donationCount || 0} donation{event.donationCount === 1 ? '' : 's'}
        </span>
      </div>
      {percent !== null && (
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-3">
          <div className="h-full bg-pink-500" style={{ width: `${percent}%` }} />
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <button
          onClick={onDonate}
          className="px-4 py-2 text-sm font-medium rounded-md bg-pink-600 text-white hover:bg-pink-700 transition-colors"
        >
          Donate
        </button>
        {canViewDonors && (
          <button
            onClick={toggleDonors}
            disabled={loadingDonations}
            className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          >
            {loadingDonations ? 'Loading…' : donations ? 'Hide donors' : 'View donors'}
          </button>
        )}
      </div>
      {donations && (
        <div className="mt-3 border-t border-gray-200 pt-3">
          {donations.length === 0 ? (
            <p className="text-sm text-gray-500">No donations yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {donations.map((d) => {
                const who = [d.donorName, d.donorCompany].filter(Boolean).join(' · ') || 'Anonymous';
                return (
                  <li key={d.id} className="py-2 flex items-start justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <div className="font-medium text-gray-900 truncate">{who}</div>
                      {d.message && <div className="text-xs text-gray-500 mt-0.5">{d.message}</div>}
                    </div>
                    <div className="font-semibold text-gray-900 whitespace-nowrap">{formatMoney(d.amount)}</div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
